import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchCurrentUser,
  invalidateUserSession,
  setUser,
  resetUser,
} from './store/userSlice';
import { fetchFeatureConfig, selectPaymentSystemEnabled } from './store/featureSlice';
import { Toaster, useToast } from './components/Toaster/use-toast';
import RequireOrgManager from './components/guards/RequireOrgManager';
import LoadingOverlay from './components/Loading/LoadingOverlay';
import PublicOnly from './components/guards/PublicOnly';
import PublicLayout from './components/layouts/PublicLayout';
import AuthenticatedLayout from './components/layouts/AuthenticatedLayout';
import Support from './pages/Support/Support';

// Pages
import LandingPage from './pages/LandingPage/LandingPage';
import AuthPage from './pages/AuthPage/AuthPage';
import ForgotPassword from './pages/AuthPage/ForgotPassword';
import ResetPassword from './pages/AuthPage/ResetPassword';
import ManagementDashboard from './pages/ManagementDashboard/ManagementDashboard';
import MeetingRoom from './pages/MeetingRoom/MeetingRoom';
import PracticeSetup from './pages/PracticeSetup/PracticeSetup';
import ConversationPrompt from './pages/ConversationPrompt/ConversationPrompt';
import InviteBridge from './pages/Invite/InviteBridge';
import UserProfile from './pages/UserProfile/UserProfile';
import TermsPage from './pages/TermsPage/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage/PrivacyPolicyPage';
import './App.css';
import AcceptGroupInviteLink from './pages/GroupInvitesBridge/AcceptInviteLink';
import { acceptGroupInviteEmail, acceptGroupInviteLink } from './api/groupApi';
import AcceptEmailInviteLink from './pages/GroupInvitesBridge/AcceptInviteEmailLink';
import { clearPendingInvite, readPendingInvite } from './utils/pendingInvites';
import PricingPage from './pages/PricingPage/PricingPage';
import BusinessPage from './pages/BusinessPage/BusinessPage';
import UserActivity from './pages/UserActivity/UserActivity';
import {
  isExplicitlyLoggedOut,
  LOGGED_OUT_KEY,
} from './utils/authSession';
import {
  clearPostAuthRedirect,
  consumePostAuthRedirect,
  DEFAULT_AUTH_REDIRECT,
  getLocationRedirect,
  readPostAuthRedirect,
} from './utils/authRedirect';
import { useAccountActivity } from './hooks/useAccountActivity';

const PUBLIC_PATHS = [
  '/',
  '/auth',
  '/forgot-password',
  '/reset-password',
  '/invite/accept',
  '/invite/accept-email',
  '/invite/group',
  '/terms',
  '/privacy-policy',
  '/support',
  '/pricing',
  '/business',
];

const isPublicPath = (pathname) =>
  PUBLIC_PATHS.some((path) =>
    pathname === path || (path !== '/' && pathname.startsWith(`${path}/`))
  );

function UnknownRouteRedirect() {
  const { data: user, loading, bootstrapped } = useSelector((state) => state.user);

  if (!bootstrapped || loading) return <LoadingOverlay />;
  return (
    <Navigate
      to={user && !isExplicitlyLoggedOut() ? DEFAULT_AUTH_REDIRECT : '/auth'}
      replace
    />
  );
}


// -----------------------------
// Main App
// -----------------------------
export default function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { data: user } = useSelector((s) => s.user);
  const { toast } = useToast();
  const paymentSystemEnabled = useSelector(selectPaymentSystemEnabled);
  useAccountActivity(Boolean(user));
  // Detect immersive (full-screen) routes
  const immersiveRoutes = [
    '/meeting/',
    '/recording-room',
    '/practice-setup',
    '/user-profile',
    '/invite/accept',
  ];
  const isImmersiveView = immersiveRoutes.some((r) =>
    location.pathname.startsWith(r)
  );
  const isConversationTypeView = location.pathname.startsWith('/conversation-type');

  // Bootstrap user on mount
  useEffect(() => {
    dispatch(fetchCurrentUser());
    dispatch(fetchFeatureConfig());
  }, [dispatch]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (!event.persisted) return;
      dispatch(invalidateUserSession());
      dispatch(fetchCurrentUser());
    };

    const handlePopState = () => {
      if (isExplicitlyLoggedOut()) dispatch(resetUser());
    };

    const handleStorage = (event) => {
      if (event.key === LOGGED_OUT_KEY && isExplicitlyLoggedOut()) {
        dispatch(resetUser());
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('storage', handleStorage);
    };
  }, [dispatch]);

  // Handle auth expiration
  useEffect(() => {
    const onExpired = () => {
      dispatch(resetUser());

      // Public routes can remain visible after the stale session is cleared.
      if (isPublicPath(location.pathname)) return;
      navigate('/auth', {
        replace: true,
        state: { from: getLocationRedirect(location) },
      });
    };

    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, [dispatch, navigate, location]);

  useEffect(() => {
    document.body.classList.toggle('no-header', isImmersiveView);
  }, [isImmersiveView]);

  useEffect(() => {
    document.body.classList.toggle('conversation-type-locked', isConversationTypeView);
    return () => {
      document.body.classList.remove('conversation-type-locked');
    };
  }, [isConversationTypeView]);


  useEffect(() => {
    if (!user) return;

    const pending = readPendingInvite();
    if (!pending) return;

    const redirect = readPostAuthRedirect() || DEFAULT_AUTH_REDIRECT;

    (async () => {
      try {
        let res;

        if (pending.mode === 'link') {
          res = await acceptGroupInviteLink(pending.token);
        } else if (pending.mode === 'email') {
          res = await acceptGroupInviteEmail(pending.token);
        }

        if (res?.groupName && res?.orgName) {
          toast({
            title: 'Welcome to the group 🎉',
            description: `You’ve joined "${res.groupName}" in ${res.orgName}.`,
          });
        } else {
          toast({
            title: 'Joined 🎉',
            description: res?.message || 'You joined the group!',
          });
        }
      } catch (error) {
        toast({
          title: 'Invite failed',
          description: error?.response?.data?.error || error?.message || 'Could not accept invite.',
          variant: 'destructive',
        });
      } finally {
        clearPendingInvite();
        navigate(redirect, { replace: true });
      }
    })();
  }, [user, navigate, toast]);

  useEffect(() => {
    if (
      user &&
      location.pathname !== '/auth' &&
      !readPendingInvite()
    ) {
      clearPostAuthRedirect();
    }
  }, [user, location.pathname]);

  
  return (
    <div id="main-body">
      <Toaster />
      <Routes>
        <Route element={<PublicLayout />}>
          <Route
            path="/"
            element={
              <PublicOnly>
                <LandingPage />
              </PublicOnly>
            }
          />
          <Route path="/support" element={<Support />} />
          <Route
            path="/pricing"
            element={
              paymentSystemEnabled
                ? <PricingPage onSelectPlan={(plan) => console.log(plan)} />
                : <Navigate to={user ? '/conversation-type' : '/'} replace />
            }
          />
          <Route path="/business" element={<BusinessPage />} />

          <Route
            path="/auth"
            element={
              <PublicOnly restoreRequestedRoute>
                <AuthPage
                  onAuthenticated={(authenticatedUser, billingIntent) => {
                    const hasPendingInvite = Boolean(readPendingInvite());
                    const redirect = hasPendingInvite
                      ? null
                      : consumePostAuthRedirect(
                          billingIntent?.from || location.state?.from
                        );

                    dispatch(setUser(authenticatedUser));

                    if (redirect) {
                      navigate(redirect, {
                        replace: true,
                        state: billingIntent || undefined,
                      });
                    }
                  }}
                />
              </PublicOnly>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicOnly>
                <ForgotPassword />
              </PublicOnly>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicOnly>
                <ResetPassword />
              </PublicOnly>
            }
          />
          <Route path="/terms" element={<PublicOnly><TermsPage /></PublicOnly>} />
          <Route path="/privacy-policy" element={<PublicOnly><PrivacyPolicyPage /></PublicOnly>} />
          <Route path="/invite/group" element={<AcceptGroupInviteLink />} />
        </Route>

        <Route element={<PublicLayout immersive />}>
          <Route path="/invite/accept" element={<InviteBridge />} />
          <Route path="/invite/accept-email" element={<AcceptEmailInviteLink />} />
        </Route>

        <Route element={<AuthenticatedLayout />}>
          <Route
            path="/org/:orgId/management"
            element={
              <RequireOrgManager redirectTo="/conversation-type">
                <ManagementDashboard />
              </RequireOrgManager>
            }
          />
          <Route
            path="/conversation-type"
            element={
              <main className="dashboard-main">
                <ConversationPrompt />
              </main>
            }
          />
          <Route
            path="/practice-history"
            element={<UserActivity view="practice" />}
          />
          <Route
            path="/training-sessions"
            element={<UserActivity view="training" />}
          />
        </Route>

        <Route element={<AuthenticatedLayout immersive />}>
          <Route
            path="/practice-setup"
            element={<PracticeSetup onBack={() => navigate('/conversation-type')} />}
          />
          <Route
            path="/meeting/:id"
            element={<MeetingRoom onBack={() => navigate('/conversation-type')} />}
          />
          <Route
            path="/user-profile"
            element={<UserProfile onBack={() => navigate('/conversation-type')} />}
          />
        </Route>

        <Route path="*" element={<UnknownRouteRedirect />} />
      </Routes>
    </div>
  );
}
